import { useCallback } from 'react'
import { isEmpty } from 'lodash'
import moment from 'moment'

import { GROUPS_TYPE_PRODUCT, CUSTOM_NAMES_PRODUCT_FILTER } from '../../constants'

const useUtils = () => {
  const getOnlyNumber = useCallback((value = '') => value.replace(/\D/g, ''), [])

  const checkStringEmpty = useCallback((value) => (
    value ?? ''
  ), [])

  const ternaryCheck = useCallback((condition, firstParam, secondParam = null) => (
    condition ? firstParam : secondParam
  ), [])

  const isBlank = useCallback((value) => {
    let data = ''

    if (typeof value === 'boolean') {
      data = `${value}`
    } else {
      data = `${value || ''}`
    }

    return isEmpty(data) || data.trim().length === 0
  }, [])

  const formatCurrency = useCallback((value) => {
    let formatValue

    const currency = new Intl.NumberFormat([], {
      style: 'currency',
      currency: 'BRL',
    })

    if (value !== '' || !isBlank(value)) {
      formatValue = currency.format(value)
    }

    return formatValue
  }, [isBlank])

  const formatCurrencyBRLToDecimal = useCallback((value) => {
    let formatValue = 0

    if (!isBlank(value)) {
      formatValue = parseFloat(String(value).slice(3).replaceAll('.', '').replaceAll(',', '.'))
    }

    return formatValue
  }, [isBlank])

  const formatCEP = useCallback((value) => !isBlank(value) && value.replace(/(\d{2})(\d{3})(\d{3})/, '$1.$2-$3'), [isBlank])

  const formatCPF = useCallback((value) => !isBlank(value) && value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4'), [isBlank])

  const formatCNPJ = useCallback((value) => !isBlank(value) && value.replace(/(\w{2})(\w{3})(\w{3})(\w{4})(\w{2})/, '$1.$2.$3/$4-$5'), [isBlank])

  const formatCPFOrCNPJ = useCallback((value, returnValue = false) => {
    if (value) {
      const normalized = value.replace(/[.\-/\s]/g, '')
      if (normalized.length === 11) {
        return formatCPF(normalized)
      }
      if (normalized.length === 14) {
        return formatCNPJ(normalized)
      }
    }
    return returnValue ? value : null
  }, [formatCPF, formatCNPJ])

  const formatSUSEP = useCallback((value) => !isBlank(value) && value.replace(/(\d{2})(\d{3})(\d{3})(\d{1})/, '$1.$2.$3-$4'), [isBlank])

  const formatPhoneOrCellphone = useCallback((value) => {
    if (!isBlank(value)) {
      let number = getOnlyNumber(value)
      number = number.replace(/^(\d{2})(\d)/g, '($1) $2')
      number = number.replace(/(\d)(\d{4})$/, '$1-$2')
      return number
    }
    return ''
  }, [isBlank, getOnlyNumber])

  const date = useCallback((value) => (value ? moment(value) : moment()), [])

  const dateHoursZero = useCallback((value) => {
    let dateHours = value ?? moment()
    if (typeof value !== 'object') {
      dateHours = moment(value)
    }

    dateHours.set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    })

    return dateHours.utc()
  }, [])

  const formatHours = useCallback((value) => value && moment(value).format('LT'), [])

  const formatDate = useCallback((value) => value && moment(value).utc().format('DD/MM/YYYY'), [])

  const formatDateApi = useCallback((value) => value && moment(value).utc().format('YYYY-MM-DD'), [])

  const formatDateNews = useCallback((value) => value && `${moment(value).format('DD')} de ${moment(value).format('MMMM YYYY')}`, [])

  const makeQueryPromise = (promise) => {
    if (promise.isFulfilled) {
      return promise
    }

    let isPending = true
    let isRejected = false
    let isFulfilled = false

    const result = promise.then((v) => {
      isFulfilled = true
      isPending = false
      return v
    }, (e) => {
      isRejected = true
      isPending = false
      throw e
    })

    result.isPending = () => isPending
    result.isRejected = () => isRejected
    result.isFulfilled = () => isFulfilled
    return result
  }

  const shortByOrder = (a, b) => {
    if (a?.type?.order < b?.type?.order) { return -1 }
    if (a?.type?.order > b?.type?.order) { return 1 }
    return 0
  }

  const shortByName = (a, b) => {
    if (a?.name < b?.name) { return -1 }
    if (a?.name > b?.name) { return 1 }
    return 0
  }

  const makeGroupProducts = useCallback((products) => {
    const productsToRender = products ?? []

    const getName = (id, name) => {
      const product = CUSTOM_NAMES_PRODUCT_FILTER.find((item) => item.id === id)
      return product?.name ?? name
    }

    return GROUPS_TYPE_PRODUCT.reduce((acc, product) => {
      const matchingProductResponse = productsToRender
        .find((productResponse) => productResponse.id === product.id)

      if (matchingProductResponse) {
        const existingType = acc.find((group) => group.type === product.type)
        const name = getName(product.id, matchingProductResponse.name)

        if (existingType) {
          existingType.products.push({ id: product.id, name })
          existingType.products.sort(shortByName)
        } else {
          acc.push({ type: product.type, products: [{ id: product.id, name }] })
        }
      }

      return acc
    }, [])
  }, [])

  return {
    date,
    dateHoursZero,
    isBlank,
    formatCPF,
    formatCEP,
    formatCNPJ,
    formatDate,
    formatHours,
    formatSUSEP,
    ternaryCheck,
    getOnlyNumber,
    formatDateApi,
    formatCurrency,
    formatDateNews,
    formatCPFOrCNPJ,
    checkStringEmpty,
    formatPhoneOrCellphone,
    formatCurrencyBRLToDecimal,
    makeQueryPromise,
    makeGroupProducts,
    shortByName,
    shortByOrder,
  }
}

export default useUtils
